/*
 * Copyright (c) 2009 Macrofocus GmbH. All Rights Reserved.
 */
package com.macrofocus.high_d.axis

import com.macrofocus.common.filter.MutableFilter
import com.macrofocus.common.format.CPFormat
import com.macrofocus.common.interval.*
import com.macrofocus.common.properties.MutableProperty
import com.macrofocus.common.scale.Transform
import org.molap.subset.OrdinalDimension

abstract class AbstractAxisModel<Row, Column> : AxisModel<Row, Column> {
    val ghostNaN = true
    val keepOutOfRange = false
    private val listeners: MutableList<com.macrofocus.high_d.axis.AxisListener> = ArrayList<com.macrofocus.high_d.axis.AxisListener>()
    override var format: CPFormat<Any?>? = null
    override fun addAxisListener(listener: AxisListener) {
        // ToDo: Unclear why this assertion is sometimes false
        //        assert !listeners.contains(listener) : "Listener " + listener + " already registered to " + this;
        listeners.add(listener)
    }

    override fun removeAxisListener(listener: com.macrofocus.high_d.axis.AxisListener) {
        listeners.remove(listener)
    }

    protected fun fireAxisChanged() {
        for (listener in listeners) {
            listener.axisChanged()
        }
    }

    protected abstract fun getFormattedValue(value: Double): String
    override fun getFormattedFloorValue(value: Double): String {
        return getFormattedValue(value)
    }

    override fun getFormattedCeilValue(value: Double): String {
        return getFormattedFloorValue(value)
    }

    protected fun addIntervalListener(interval: BoundedInterval) {
        interval.addBoundedIntervalListener(object : BoundedIntervalListener {
            override fun boundedIntervalChanged(event: BoundedIntervalEvent) {
                fireAxisChanged()
            }
        })
        interval.addIntervalListener(object : IntervalListener {
            override fun intervalChanged(event: IntervalEvent) {
                fireAxisChanged()
            }
        })
    }

    protected fun registerInterval(interval: BoundedInterval) {
        interval.addIntervalListener(object : IntervalListener {
            override fun intervalChanged(event: IntervalEvent) {
                if (dimension != null) {
                    if (interval.isFullRange) {
                        dimension!!.filterAll()
                    } else {
                        val from: Any?
                        val to: Any?
                        if (!interval.isInvertedScale) {
                            from = getOriginalCeilValue(interval.start)
                            to = getOriginalFloorValue(interval.end)
                        } else {
                            from = getOriginalCeilValue(interval.end)
                            to = getOriginalFloorValue(interval.start)
                        }
                        //                        System.err.println("Filtering range " + from + " (" + event.getNewStart() + ") - " + to + " (" + event.getNewEnd() + "): " + interval);
                        @Suppress("UNCHECKED_CAST")
                        (dimension as OrdinalDimension<Row, *, Any?>).filterRange(from, to)
                    }
                } else {
                    incrementalProcessing(interval, event)
                }
            }
        })
        interval.addBoundedIntervalListener(object : BoundedIntervalListener {
            override fun boundedIntervalChanged(event: BoundedIntervalEvent) {
                if (dimension != null) {
                    if (interval.isFullRange) {
                        dimension!!.filterAll()
                    } else {
                        val from: Any?
                        val to: Any?
                        if (!interval.isInvertedScale) {
                            from = getOriginalCeilValue(interval.start)
                            to = getOriginalFloorValue(interval.end)
                        } else {
                            from = getOriginalCeilValue(interval.end)
                            to = getOriginalFloorValue(interval.start)
                        }
                        @Suppress("UNCHECKED_CAST")
                        (dimension as OrdinalDimension<Row, *, Any?>).filterRange(from, to)
                    }
                } else {
                    fullProcessing(interval)
                }
            }
        })
    }

    override val binCount: MutableProperty<Int>?
        get() = null
    override val binType: MutableProperty<BinType>?
        get() = null

    override val scale: MutableProperty<Transform>?
        get() = null

    private fun incrementalProcessing(interval: BoundedInterval, currentRangeSliderEvent: IntervalEvent) {
        val lastMin: Double
        val lastMax: Double
        val diffMin: Double
        val diffMax: Double
        val currentMin: Double
        val currentMax: Double
        val isMaxRange: Boolean
        val wasMaxRange: Boolean
        val isMinRange: Boolean
        val wasMinRange: Boolean
        val fullProcessing: Boolean
        if (!interval.isInvertedScale) {
            currentMin = currentRangeSliderEvent.newStart
            currentMax = currentRangeSliderEvent.newEnd
            lastMin = currentRangeSliderEvent.oldStart
            lastMax = currentRangeSliderEvent.oldEnd

            // Negative range
            fullProcessing = if (lastMin <= lastMax && currentMin > currentMax) {
                true
            } else lastMin >= lastMax && currentMin < currentMax
            wasMinRange = keepOutOfRange && currentRangeSliderEvent.oldStart == interval.minimum
            wasMaxRange = keepOutOfRange && currentRangeSliderEvent.oldEnd == interval.maximum
            isMinRange = keepOutOfRange && currentRangeSliderEvent.newStart == interval.minimum
            isMaxRange = keepOutOfRange && currentRangeSliderEvent.newEnd == interval.maximum
            diffMin = lastMin - currentRangeSliderEvent.newStart
            diffMax = lastMax - currentRangeSliderEvent.newEnd
        } else {
            currentMin = currentRangeSliderEvent.newEnd
            currentMax = currentRangeSliderEvent.newStart
            lastMin = currentRangeSliderEvent.oldEnd
            lastMax = currentRangeSliderEvent.oldStart

            // Negative range
            fullProcessing = if (lastMin >= lastMax && currentMin < currentMax) {
                true
            } else lastMin <= lastMax && currentMin > currentMax
            wasMinRange = keepOutOfRange && currentRangeSliderEvent.oldEnd == interval.maximum
            wasMaxRange = keepOutOfRange && currentRangeSliderEvent.oldStart == interval.minimum
            isMinRange = keepOutOfRange && currentRangeSliderEvent.newEnd == interval.maximum
            isMaxRange = keepOutOfRange && currentRangeSliderEvent.newStart == interval.minimum
            diffMin = lastMin - currentRangeSliderEvent.newEnd
            diffMax = lastMax - currentRangeSliderEvent.newStart
        }
        if (fullProcessing) {
            fullProcessing(interval)
        } else {
            val positive: Boolean
            if (!interval.isInvertedScale) {
                positive = interval.start <= interval.end
            } else {
                positive = interval.start >= interval.end
            }
            val model: com.macrofocus.high_d.axis.AxisModel<Row, Column> = this
            val filter: MutableFilter<Row> = filter!!
            val toFilter: MutableList<Row> = ArrayList<Row>()
            val toUnfilter: MutableList<Row> = ArrayList<Row>()
            if (diffMin > 0) {
                // Let's unfilter
                for (row in model) {
                    val value: Number? = model.getValue(row)
                    if (value != null) {
                        val test: Boolean
                        val v = value.toDouble()
                        test = if (positive) {
                            v <= lastMin && (v >= currentMin || isMinRange)
                        } else {
                            v < lastMin && (v >= currentMin || isMinRange)
                        }
                        if (test) {
                            if (filter.isFilteredBy(row, model)) {
                                toUnfilter.add(row)
                            }
                        }
                    } else {
                        if (ghostNaN && isMaxRange && isMinRange) {
                            if (filter.isFilteredBy(row, model)) {
                                toUnfilter.add(row)
                            }
                        }
                    }
                }
            } else if (diffMin < 0) {
                // Let's filter
                for (r in model) {
                    val value: Number? = model.getValue(r)
                    if (value != null) {
                        val v = value.toDouble()
                        val test: Boolean
                        test = if (positive) {
                            (v >= lastMin || wasMinRange) && v < currentMin
                        } else {
                            (v >= lastMin || wasMinRange) && v <= currentMin
                        }
                        if (test) {
                            if (!filter.isFilteredBy(r, model)) {
                                toFilter.add(r)
                            }
                        }
                    } else {
                        if (ghostNaN && wasMaxRange && wasMinRange) {
                            if (!filter.isFilteredBy(r, model)) {
                                toFilter.add(r)
                            }
                        }
                    }
                }
            }
            if (diffMax > 0) {
                // Let's filter
                for (r in this) {
                    val value: Number? = getValue(r)
                    if (value != null) {
                        val v = value.toDouble()
                        val test: Boolean
                        test = if (positive) {
                            (v <= lastMax || wasMaxRange) && v > currentMax
                        } else {
                            (v <= lastMax || wasMaxRange) && v >= currentMax
                        }
                        if (test) {
                            if (!filter.isFilteredBy(r, this)) {
                                toFilter.add(r)
                            }
                        }
                    } else {
                        if (ghostNaN && wasMaxRange && wasMinRange) {
                            if (!filter.isFilteredBy(r, this)) {
                                toFilter.add(r)
                            }
                        }
                    }
                }
            } else if (diffMax < 0) {
                // Let's unfilter
                for (r in model) {
                    val value: Number? = model.getValue(r)
                    if (value != null) {
                        val v = value.toDouble()
                        val test: Boolean
                        test = if (positive) {
                            v > lastMax && (v <= currentMax || isMaxRange)
                        } else {
                            v >= lastMax && (v <= currentMax || isMaxRange)
                        }
                        if (test) {
                            if (filter!!.isFilteredBy(r, model)) {
                                toUnfilter.add(r)
                            }
                        }
                    } else {
                        if (ghostNaN && isMaxRange && isMinRange && diffMin <= 0) {
                            if (filter!!.isFilteredBy(r, model)) {
                                toUnfilter.add(r)
                            }
                        }
                    }
                }
            }
            if (toFilter.size > 0) {
                filter.setFilteredIterable(toFilter, true, model)
            }
            if (toUnfilter.size > 0) {
                filter.setFilteredIterable(toUnfilter, false, model)
            }
        }
    }

    private fun fullProcessing(interval: BoundedInterval) {
        val isMinRange = interval.start == interval.minimum
        val isMaxRange = interval.end == interval.maximum
        val fullRange = isMinRange && isMaxRange
        val positive: Boolean = interval.start <= interval.end
        val toFilter: MutableList<Row> = ArrayList<Row>()
        val toUnfilter: MutableList<Row> = ArrayList<Row>()
        val model: com.macrofocus.high_d.axis.AxisModel<Row, Column> = this
        val filter: MutableFilter<Row> = filter!!
        for (r in model) {
            val value: Number? = model.getValue(r)
            if (value != null) {
                val v = value.toDouble()
                val shouldBeFiltered: Boolean
                shouldBeFiltered = if (!interval.isInvertedScale) {
                    if (positive) {
                        !(fullRange && keepOutOfRange) && (v < interval.start ||
                                v > interval.end)
                    } else {
                        !(fullRange && keepOutOfRange) && v <= interval.start &&
                                v >= interval.end
                    }
                } else {
                    if (positive) {
                        !(fullRange && keepOutOfRange) && v >= interval.start &&
                                v <= interval.end
                    } else {
                        !(fullRange && keepOutOfRange) && (v > interval.start ||
                                v < interval.end)
                    }
                }
                if (shouldBeFiltered && !filter.isFilteredBy(r, model)) {
                    toFilter.add(r)
                } else if (!shouldBeFiltered && filter.isFilteredBy(r, model)) {
                    toUnfilter.add(r)
                }
            } else {
                val shouldBeFiltered = !fullRange
                if (shouldBeFiltered && !filter.isFilteredBy(r, model)) {
                    toFilter.add(r)
                } else if (!shouldBeFiltered && filter.isFilteredBy(r, model)) {
                    toUnfilter.add(r)
                }
            }
        }
        if (toFilter.size > 0) {
            filter.setFilteredIterable(toFilter, true, model)
        }
        if (toUnfilter.size > 0) {
            filter.setFilteredIterable(toUnfilter, false, model)
        }
    }
}